import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { listCharterPromotion, upsertCharterPromotion } from '~/services/CharterPromotionService';

import type {
  CharterPromotionType,
  ListCharterPromotionPricingInventoryParams,
  UpsertCharterPromotionPricingInventoryBody,
} from '~/types/charterPromotionPricingInventory';

import { charterPromotionQueryKeys } from './charterPromotionQueryKeys';

export function useGetCharterPromotionList(
  params: ListCharterPromotionPricingInventoryParams = {},
  options: { enabled?: boolean } = {},
) {
  const { enabled = true } = options;

  return useQuery({
    queryKey: charterPromotionQueryKeys.list(params),
    queryFn: async ({ signal }) => {
      const { promotionType = 'HUNDRED_DOLLAR', ...filters } = params;
      return await listCharterPromotion(promotionType, filters, { signal });
    },
    // A server-paginated grid must retain its known row count while the next page loads.
    // Otherwise it briefly receives zero rows, clamps its page back to zero, and never requests page two.
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useUpsertCharterPromotion(promotionType: CharterPromotionType = 'HUNDRED_DOLLAR') {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpsertCharterPromotionPricingInventoryBody) => {
      return await upsertCharterPromotion(promotionType, body);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: charterPromotionQueryKeys.all });
    },
  });
}
